/**
 * AI 時代のキャリア診断 (/ai-career-diagnosis) の問いと、答えから結果を組む決まり。
 *
 * 結果に出すのは、今の仕事で AI に任せやすくなる作業と人に残りやすい作業、足すと強いこと 3 つ、道 3 つ。
 * 道は 3 つとも見せ、答えに近い 1 つに印を付ける。合否、点数、「仕事が何割なくなる」は出さない。
 *
 * 仕事ごとの作業の文は出典の無い目安で、結果の頁でもそう書く。数を出すのは arXiv 2303.10130 の 1 つだけ (AI_SOURCE)。
 * 答えは住所の引数 (j, t, u, b, g, h) に載せる。サーバーには何も書かない (頁を見た記録の住所には載る)
 */
import type { JdIconName } from "@/components/lab/job-diagnosis-icons";

export const AI_DIAGNOSIS_PATH = "/ai-career-diagnosis";
export const AI_DIAGNOSIS_RESULT_PATH = "/ai-career-diagnosis/result";

export type AiChoice = { label: string; icon: JdIconName };
export type AiQuestionId = "j" | "t" | "u" | "b" | "g" | "h";
export type AiQuestion = {
  id: AiQuestionId;
  /** 問いの字。句で 2 行に分ける */
  lines: readonly [string, string];
  choices: readonly AiChoice[];
};

export const AI_QUESTIONS: readonly AiQuestion[] = [
  {
    id: "j",
    lines: ["今の仕事に、", "いちばん近いのは？"],
    choices: [
      { label: "事務・経理", icon: "doc-list" },
      { label: "営業・販売", icon: "bag" },
      { label: "接客・サービス", icon: "person" },
      { label: "企画・マーケ", icon: "trend" },
      { label: "制作・デザイン", icon: "window" },
      { label: "医療・介護・教育", icon: "shield" },
    ],
  },
  {
    id: "t",
    lines: ["1 日の仕事で、", "いちばん時間を使うのは？"],
    choices: [
      { label: "書類や資料をつくる", icon: "list" },
      { label: "人と話す、調整する", icon: "chat" },
      { label: "数字を集める、入力する", icon: "server" },
      { label: "手や体を動かす", icon: "bolt" },
    ],
  },
  {
    id: "u",
    lines: ["AI を仕事で", "使っていますか？"],
    choices: [
      { label: "まだ使っていない", icon: "sprout" },
      { label: "調べ物に少し", icon: "book" },
      { label: "文や資料づくりに毎週", icon: "calendar" },
      { label: "作業の流れに組み込んでいる", icon: "spark" },
    ],
  },
  {
    id: "b",
    lines: ["何かを作った", "ことはありますか？"],
    choices: [
      { label: "まだ無い", icon: "sprout" },
      { label: "表計算の関数やマクロ", icon: "list" },
      { label: "ノーコードで道具を作った", icon: "window" },
      { label: "コードを書いて作った", icon: "code" },
    ],
  },
  {
    id: "g",
    lines: ["これから、", "いちばん変えたいのは？"],
    choices: [
      { label: "仕事そのものを変えたい", icon: "flag" },
      { label: "今の職場で頼られたい", icon: "person" },
      { label: "収入の柱をもう 1 つ持ちたい", icon: "trend" },
      { label: "まだ決めていない", icon: "tag" },
    ],
  },
  {
    id: "h",
    lines: ["学ぶのに使える時間は、", "週にどれくらい？"],
    choices: [
      { label: "3 時間より少ない", icon: "clock" },
      { label: "3〜7 時間", icon: "clock" },
      { label: "7〜15 時間", icon: "clock" },
      { label: "15 時間より多い", icon: "clock" },
    ],
  },
];

export type AiAnswers = Record<AiQuestionId, number>;

/** 答えを住所の引数から読む。欠けている、または範囲の外の値が 1 つでもあれば null (結果を組まない) */
export function parseAiAnswers(params: URLSearchParams): AiAnswers | null {
  const out: Partial<AiAnswers> = {};
  for (const q of AI_QUESTIONS) {
    const raw = params.get(q.id);
    if (raw === null || !/^\d$/.test(raw)) return null;
    const n = Number(raw);
    if (n >= q.choices.length) return null;
    out[q.id] = n;
  }
  return out as AiAnswers;
}

/** 記事の中の 1 問目 (?j=) だけを読む。範囲の外なら null */
export function parseFirstAnswer(params: URLSearchParams): number | null {
  const raw = params.get("j");
  if (raw === null || !/^\d$/.test(raw)) return null;
  const n = Number(raw);
  return n < AI_QUESTIONS[0].choices.length ? n : null;
}

/** 入口で付いた経路の印。結果の頁へも運び、そこから LINE と相談の住所へ withUtm で足す */
const CARRY_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content"] as const;
const SAFE_VALUE = /^[A-Za-z0-9_.-]{1,64}$/;

export function aiResultPath(answers: AiAnswers, carry?: URLSearchParams): string {
  const params = new URLSearchParams();
  for (const q of AI_QUESTIONS) params.set(q.id, String(answers[q.id]));
  for (const key of CARRY_KEYS) {
    const value = carry?.get(key);
    if (value && SAFE_VALUE.test(value)) params.set(key, value);
  }
  return `${AI_DIAGNOSIS_RESULT_PATH}?${params.toString()}`;
}

type JobTasks = { ai: readonly string[]; human: readonly string[] };

/** 仕事ごとの作業の目安。出典は無いので、結果の頁で「目安」と書く。並びは 1 問目の選択肢と同じ */
export const JOB_TASKS: readonly JobTasks[] = [
  {
    ai: ["定型のメールや文書の下書き", "書類から数字を読み取って写す作業", "会議の記録のまとめ"],
    human: ["例外を見つけて判断する", "社内の人どうしをつなぐ調整", "仕組みそのものを見直す提案"],
  },
  {
    ai: ["提案書や見積もりの下書き", "商談の記録の整理", "相手の会社の下調べ"],
    human: ["相手の本音を聞き取る", "条件の交渉と約束", "長い付き合いを育てる"],
  },
  {
    ai: ["予約や問い合わせの最初の受け答え", "シフトや在庫の表づくり", "案内文や掲示物の下書き"],
    human: ["目の前のお客様への気配り", "その場の困りごとの解決", "チームの空気づくり"],
  },
  {
    ai: ["調べ物の下調べと要約", "広告文や記事の案出し", "数字の報告書づくり"],
    human: ["誰に何を届けるかを決める", "結果から次の手を選ぶ", "社内外の合意づくり"],
  },
  {
    ai: ["ラフ案を何通りも出す", "素材の加工や書き出し", "仕様の整理"],
    human: ["何を良しとするかの判断", "依頼主の意図をくみ取る", "仕上げの質を決める"],
  },
  {
    ai: ["記録や報告書の下書き", "資料や教材の準備", "予定や連絡の整理"],
    human: ["相手の様子を見て判断する", "信頼の関係をつくる", "安全に関わる最後の判断"],
  },
];

/** 2 問目 (時間を使う作業) ごとの一言。並びは 2 問目の選択肢と同じ */
export const TASK_NOTES: readonly string[] = [
  "AI が下書きを出し、あなたが直して仕上げる形に変わりやすい作業です。直す目と、頼み方の型があるほど速くなります。",
  "人に残りやすい作業です。話す前の準備と、話したあとの記録を AI に任せると、人と向き合う時間を増やせます。",
  "手で写す作業は、表や小さな道具に任せる形に変わりやすい作業です。道具を作れる人は、職場で頼られやすくなります。",
  "作業そのものは人に残りやすい仕事です。変わりやすいのは予定、記録、連絡といった周りの事務で、そこから楽にできます。",
];

/** 一次の出典。数を出すのはこの 1 つだけ */
export const AI_SOURCE = {
  text: "米国の職業を作業に分けて調べた研究では、働く人の約 80% が、仕事の作業のうち 10% 以上に大規模言語モデルの影響を受けうると見積もっています。いつ起きるかの予測はしていません。",
  cite: "Eloundou, Manning, Mishkin, Rock「GPTs are GPTs」(2023)",
  url: "https://arxiv.org/abs/2303.10130",
} as const;

export type AiPathKey = "career" | "inhouse" | "side";
export type AiPath = {
  key: AiPathKey;
  art: string;
  title: string;
  sub: string;
  fit: string;
  step: string;
  to: string;
  go: string;
};

export const AI_PATHS: readonly AiPath[] = [
  {
    key: "career",
    art: "/assets/course-bands/career-path-guide-career-41acaf5962.webp",
    title: "エンジニアに転職する",
    sub: "作る側を仕事にする道",
    fit: "仕事そのものを変えたい人。まとまった学ぶ時間を、続けて取れる人",
    step: "転職ロードマップの 6 つの段のうち、今どこにいるかを見る",
    to: "/career-change/diagnosis",
    go: "転職診断で今の段を見る",
  },
  {
    key: "inhouse",
    art: "/assets/course-bands/claude-code-workflow-gear-7c2bc16d7d.webp",
    title: "今の職場で、AI を使う側になる",
    sub: "転職しなくても始められる道",
    fit: "今の職場で頼られたい人。週に数時間から始めたい人",
    step: "毎週くり返している作業を 1 つ選び、AI に渡す流れを作る",
    to: "/learn/courses/generative-ai-basics",
    go: "生成 AI の基礎を読む",
  },
  {
    key: "side",
    art: "/assets/course-bands/generative-ai-basics-build-942a8b6cd7.webp",
    title: "副業で、作った物を試す",
    sub: "仕事の外で、小さく試す道",
    fit: "収入の柱をもう 1 つ持ちたい人。まず小さく試したい人",
    step: "AI を使って、人に見せられる小さな物を 1 つ作る",
    to: "/side-income",
    go: "副業の始め方を読む",
  },
];

/** 変えたいこと (5 問目) から、いちばん近い道。決めていない人は、ほかの 2 つにもつながる「今の職場」にする */
export function nearestPath(answers: Pick<AiAnswers, "g">): AiPathKey {
  return (["career", "inhouse", "side", "inhouse"] as const)[answers.g] ?? "inhouse";
}

export type AiAdd = { title: string; text: string };

/** 足すと強いこと 3 つ。AI の使い方 (3 問目) と作った物 (4 問目) で 2 つが替わり、3 つ目は誰にも出す */
export function aiAdds(answers: Pick<AiAnswers, "u" | "b">): readonly AiAdd[] {
  return [
    answers.u <= 1
      ? {
          title: "頼み方を型にする",
          text: "役割、形、長さを決めて頼むだけで、AI の答えは仕事で使える形に近づきます。記事で読んだやり方が、その入口です。",
        }
      : {
          title: "毎週の作業を、AI に渡す流れにする",
          text: "思いついたときに使う段から、決まった作業を毎週 AI に渡す段へ。職場で人に教えられるのも、この段からです。",
        },
    answers.b <= 1
      ? {
          title: "小さな道具を、自分で作る",
          text: "AI が書いたコードを読んで直せると、任せられる範囲が広がります。表計算の関数の次の一歩です。",
        }
      : {
          title: "作った物を、人に見せられる形にする",
          text: "動く物を公開し、何のために作ったかを書く。転職でも副業でも、これが自分の名刺になります。",
        },
    {
      title: "AI の答えを確かめる目",
      text: "AI はもっともらしく間違えます。間違いに気づいて直せる人が、AI を使う側に立てます。",
    },
  ];
}

/** 道の下に添える一言。当てはまるときだけ */
export function pathHints(answers: Pick<AiAnswers, "g" | "h">): readonly string[] {
  const near = nearestPath(answers);
  const hints: string[] = [];
  if (answers.g === 3) {
    hints.push("まだ決めていないなら、今の職場で AI を使う道から始めると、ほかの 2 つの道にもつながります。");
  }
  if (answers.h === 0 && near === "career") {
    hints.push("学ぶ時間が週 3 時間より少ないなら、今の職場で使う道で時間を作ってから転職を考える手もあります。");
  }
  if (near === "side") {
    hints.push("副業は、すぐに稼げると約束できる道ではありません。まず作れる物を 1 つ増やすところからです。");
  }
  return hints;
}

/** 結果の見出し。言い切らず「〜そうです」で止める */
export function resultHeadline(answers: Pick<AiAnswers, "j">): readonly string[] {
  const job = AI_QUESTIONS[0].choices[answers.j]?.label ?? AI_QUESTIONS[0].choices[0].label;
  return [`${job}の仕事は、`, "AI に任せる作業と、", "あなたが決める作業に", "分かれていきそうです"];
}
